import { useEffect, useMemo } from "react";
import { useLatest, useSetState } from "ahooks";
import { message } from "antd";
import { t } from "i18next";
import { twMerge } from "tailwind-merge";
import Loading from "@src/ui/Loading";
import { useSerializeCallback } from "@kalodata/tools";
import { confirmPublish, UserPublishResultResponse } from "@apis/http/api";
import { PostingTips, type PostingPhase } from "./PostingTips";
import { OptimizationSuggestionsModal, PostFailModal, PreCheckFailModal, isAuthFailCode } from "./PostResultModals";
import report from "@report";
import { MessageGo2Link } from "@components/MessageGo2Link";
import { UserPublishResultResponseStatus } from "@apis/generated/openapi";
import { createPublishResultPoll$ } from "./publishResultPolling";

type Decision = "needConfirm" | "preCheckFail" | "postFail" | "pollFail" | undefined;
const POLL_ERROR_REASON = "Unable to check publishing status. Please retry.";

const STATUS_PHASE_MAP: Partial<Record<NonNullable<UserPublishResultResponse["status"]>, PostingPhase>> = {
  INIT: "submitting",
  UPLOADING: "submitting",
  PRE_CHECKING: "analyzing",
  PRE_CHECK_SUCCESS: "analyzing",
  PUBLISHING: "posting",
};

export interface PostRecordProbeProps {
  /** 是否是定时发布的视频 */
  isScheduled: boolean;
  postRecordId: number;
  className?: string;
  /** 父级 submit 重试中（来自 useSerializeCallback.isPending()） */
  retrying?: boolean;
  /** 父级 OAuth 弹窗中 */
  reconnecting?: boolean;
  /**
   * Debug 用：当传入时短路真实轮询，直接把 mock 喂给状态机以渲染对应的 UI 分支。
   * 仅在 dev/staging 环境的调试入口设置（见 debugMocks.ts）。
   */
  mockResult?: UserPublishResultResponse;
  /** 终态 SUCCESS 时回调，关闭外层弹窗 */
  onSuccess?: () => void;
  /** 用户主动放弃当前发布记录回到表单（保留表单字段） */
  onBackToEdit?: () => void;
  /** 已发布阶段失败后用相同表单重新 submitPublish */
  onRetry?: () => void;
  /** 授权过期时调用（重新走 OAuth） */
  onReconnect?: () => Promise<unknown> | void;
}

export const PostRecordProbe = (props: PostRecordProbeProps) => {
  const { postRecordId, className, retrying, reconnecting, mockResult, onSuccess, onBackToEdit, onRetry, onReconnect } =
    props;
  const onSuccessFn = useLatest(onSuccess);
  const onBackToEditFn = useLatest(onBackToEdit);
  const onRetryFn = useLatest(onRetry);
  const onReconnectFn = useLatest(onReconnect);

  const [{ res, decision, pollEpoch, pollErrorReason }, setState] = useSetState<{
    res: UserPublishResultResponse | undefined;
    decision: Decision;
    pollEpoch: number;
    pollErrorReason?: string;
  }>({
    res: undefined,
    decision: undefined,
    pollEpoch: 0,
    pollErrorReason: undefined,
  });

  useEffect(() => {
    setState({ res: undefined, decision: undefined, pollErrorReason: undefined });

    let finalHandled = false;

    const applyResult = (next: UserPublishResultResponse) => {
      setState({ res: next });
      const { status } = next;
      const isPendingPublish = status === UserPublishResultResponseStatus.PENDING_PUBLISH;
      const isFinal = next.terminal || isPendingPublish;

      if (isFinal) {
        if (finalHandled) return;
        finalHandled = true;

        if (status === UserPublishResultResponseStatus.SUCCESS) {
          message.success(
            <MessageGo2Link link="/library" linkText="Go check it">
              Content posted successfully!
            </MessageGo2Link>,
          );
          onSuccessFn.current?.();
        } else if (isPendingPublish) {
          message.success(
            <MessageGo2Link link="/social-publisher?tab=scheduled" linkText="View schedule">
              Post scheduled successfully!
            </MessageGo2Link>,
          );
          onSuccessFn.current?.();
        } else if (next.status === UserPublishResultResponseStatus.PRE_CHECK_FAIL) {
          setState({ decision: "preCheckFail" });
        } else {
          // FAIL（含发布失败、下载/上传失败、轮询超时等）
          setState({ decision: "postFail" });
        }
      } else if (next.needConfirm) {
        setState({ decision: "needConfirm" });
      } else if (next.failCode || next.failReason) {
        const { failCode, failReason } = next;
        message.error(t(failReason || failCode || "Unknown error"));
        console.log(next);
        report.event("post_record_probe_fail", {
          kalo_json: next,
        });
      }
    };

    // Debug 短路：直接应用 mock，跳过真实轮询。
    if (mockResult) {
      applyResult(mockResult);
      return;
    }

    const sub = createPublishResultPoll$(postRecordId).subscribe({
      next: applyResult,
      error: (error) => {
        report.event("post_record_probe_poll_error", {
          kalo_json: {
            postRecordId,
            message: error instanceof Error ? error.message : String(error),
          },
        });
        setState({ decision: "pollFail", pollErrorReason: POLL_ERROR_REASON });
      },
    });

    return () => sub.unsubscribe();
    // onSuccessFn 为 useLatest ref、setState 为稳定引用，故不纳入依赖以避免 effect 重启叠加订阅
  }, [postRecordId, pollEpoch, mockResult]);

  // 二次确认：调 confirmPublish 后清掉 decision 并触发新一轮轮询
  const handleContinuePost = useSerializeCallback(async () => {
    await confirmPublish({ id: postRecordId });
    setState((prev) => ({ decision: undefined, pollEpoch: prev.pollEpoch + 1 }));
  });

  const handleRetryPolling = () => {
    setState((prev) => ({
      decision: undefined,
      pollErrorReason: undefined,
      pollEpoch: prev.pollEpoch + 1,
    }));
  };

  const handleReconnect = async () => {
    try {
      await onReconnectFn.current?.();
    } finally {
      // reconnect 之后回到表单让用户重新发布
      onBackToEditFn.current?.();
    }
  };

  const phase: PostingPhase = useMemo(() => {
    if (!res || !res.status) return "submitting";
    return STATUS_PHASE_MAP[res.status] ?? "submitting";
  }, [res]);

  const issues = res?.issues ?? [];
  const failCode = res?.failCode;
  const failReason = res?.failReason ?? pollErrorReason;

  const showLoading = !decision;

  return (
    <div
      className={twMerge(
        "flex flex-col items-center justify-center gap-[16px] px-[16px] pt-[24px] pb-[16px]",
        className,
      )}
    >
      {showLoading ? (
        <>
          <div className="flex size-[56px] items-center justify-center">
            <Loading />
          </div>
          <PostingTips phase={phase} />
        </>
      ) : null}

      <OptimizationSuggestionsModal
        open={decision === "needConfirm"}
        issues={issues}
        confirming={handleContinuePost.isPending()}
        onClose={() => onBackToEditFn.current?.()}
        onContinue={handleContinuePost}
      />

      <PreCheckFailModal
        open={decision === "preCheckFail"}
        failCode={failCode}
        failReason={failReason}
        issues={issues}
        reconnecting={!!reconnecting}
        onClose={() => onBackToEditFn.current?.()}
        onReconnect={() => {
          if (isAuthFailCode(failCode)) {
            void handleReconnect();
          } else {
            onBackToEditFn.current?.();
          }
        }}
      />

      <PostFailModal
        open={decision === "postFail" || decision === "pollFail"}
        failReason={failReason}
        retryText={decision === "pollFail" ? t("Check again") : undefined}
        retrying={decision === "postFail" && !!retrying}
        onClose={() => onBackToEditFn.current?.()}
        onRetry={() => {
          if (decision === "pollFail") {
            handleRetryPolling();
            return;
          }
          onRetryFn.current?.();
        }}
      />
    </div>
  );
};
